import { resolveMediaUrl } from '../../../utils/helpers';
import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { Search, Users, ShieldAlert, Wrench } from 'lucide-react';
import toast from 'react-hot-toast';
import { academyAPI } from '../../../services/api';
import PaymentStatusBadge from '../../../payments/components/PaymentStatusBadge';
import { needsCardPayment } from '../../../payments/services/PaymentMethods';
import UnpaidPaymentActions from '../../../payments/components/PaymentUnpaidActions';
import { useStripeReturn } from '../../../payments/services/PaymentStripeHooks';
import PaymentResultBanner from '../../../payments/components/PaymentResultBanner';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

function Avatar({ member }) {
  const initials = `${member.firstName?.[0] || ''}${member.lastName?.[0] || ''}`.toUpperCase();
  if (member.profileImage) {
    return (
      <img
        src={resolveMediaUrl(member.profileImage)}
        alt={`${member.firstName} ${member.lastName}`}
        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
        onError={e => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=3b82f6&color=fff&size=64`; }}
      />
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold text-xs flex-shrink-0">
      {initials || '?'}
    </div>
  );
}

const ProgramRosterPanel = ({ program, academyId, academySlug, userRole, canManagePrograms = false }) => {
  const { theme } = useTheme();
  const { user } = useAuth();

  const [enrollments, setEnrollments]       = useState([]);
  const [loading, setLoading]               = useState(true);
  const [search, setSearch]                 = useState('');
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [removing, setRemoving]             = useState(null);
  // Which row has its stuck-payment panel open. One at a time: the panel asks
  // Stripe as it opens, and several open at once is several questions nobody
  // asked for.
  const [recoveringId, setRecoveringId]     = useState(null);

  // Was `userRole === 'coach'`, which is a job title rather than a permission:
  // a coach without manage_programs saw buttons that 403'd, and staff titled
  // anything else ("director of athletics") were locked out of work they had
  // been granted. Roles here are free text, so they can never be the gate.
  const canManage = canManagePrograms;
  // The owner and staff are sent the whole roster; anybody else — a parent back
  // from checkout — only the places they made, so seats counted from those
  // would be wrong.
  const seesEveryone = !!userRole && userRole !== 'member';

  // Stripe returns people to the tab they left from, where the row will have
  // flipped to Paid. `view=enrolled` matters: without it the return lands on
  // the program's Overview and the payer never sees the row they just paid.
  const payReturnPath = academySlug
    ? `/academy/${academySlug}?tab=programs&program=${program?.id}&view=enrolled`
    : '/';

  /**
   * Whose card could actually pay this.
   *
   * Deliberately not the shared canPayFor() from paymentMethods: that one reads
   * createdBy/participantId, which are the tournament participant's field names.
   * An enrollment names the same two people as enrolledBy/userId, and quietly
   * reading one field when the other is missing is how a check like this ends
   * up passing for everybody. Mirrors payerIds() on the server.
   */
  const canPayFor = (enr) =>
    !!user?.id && (enr.enrolledBy === user.id || enr.userId === user.id);

  /**
   * Staff can withdraw anybody; everyone else can withdraw their own place.
   *
   * Gated on canManage alone, a member who had enrolled and paid had no way to
   * leave — they had to ask the venue. Leaving is not an administrative act,
   * and the same two people who can PAY for a place are the ones who own it:
   * whoever is enrolled, and the parent who enrolled them and holds the fee.
   * Mirrors the server, which now makes the same distinction.
   */
  const canWithdraw = (enr) => canManage || canPayFor(enr);

  const reloadEnrollments = () =>
    academyAPI.enrollments.list(academyId, program.id)
      .then(data => setEnrollments(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to reload enrolled students'));

  // Somebody paying from this roster comes back to this roster, so the check
  // that settles the session has to live here too. It was only wired into the
  // registration flow and the tournament page, which is why paying from here
  // went through Stripe and left the row saying Unpaid: nothing on the page
  // the payer landed on ever asked Stripe what had happened. The webhook
  // settles it eventually in a deployed environment, but never locally, and
  // "eventually" is not what the person staring at the row needs.
  const {
    result: paymentResult, verifying: verifyingPayment, dismiss: dismissPaymentResult,
  } = useStripeReturn({ onPaid: reloadEnrollments });

  useEffect(() => {
    if (!academyId || !program?.id) return;
    setLoading(true);
    academyAPI.enrollments.list(academyId, program.id)
      .then(data => setEnrollments(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load enrolled students'))
      .finally(() => setLoading(false));
  }, [academyId, program?.id]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return enrollments;
    return enrollments.filter(e =>
      `${e.firstName} ${e.lastName}`.toLowerCase().includes(q)
    );
  }, [enrollments, search]);

  /**
   * Give up a place, and say what became of the fee.
   *
   * The server does both in one call: releaseOrderFor withdraws an unpaid
   * order, and a paid one is reversed through the shared PaymentRefund, which
   * hands back `message` — the sentence written for whatever actually happened.
   * That answer used to be discarded here, so staff removed somebody who had
   * paid and were told only "removed from program", with nothing about the
   * money. A refund that failed is the one they most needed to see.
   */
  const handleWithdraw = async (memberId, name) => {
    setRemoving(memberId);
    try {
      const res = await academyAPI.enrollments.unenroll(academyId, program.id, memberId);
      setEnrollments(prev => prev.filter(e => e.memberId !== memberId));
      setConfirmRemoveId(null);

      // Shown as a warning that stays put, not a success that slides away —
      // a refund that did not go through needs somebody to act on it.
      if (res?.refund && res.refund.ok === false) {
        toast(`${name} withdrawn. ${res.message}`, { icon: '⚠️', duration: 12000 });
      } else if (res?.message) {
        toast.success(`${name} withdrawn. ${res.message}`, { duration: 8000 });
      } else {
        toast.success(`${name} withdrawn from the programme`);
      }
    } catch (err) {
      toast.error(err?.message || 'Could not withdraw that student');
    } finally {
      setRemoving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div>
      {/* Coming back from Stripe, the payer needs to be told what happened
          before they go hunting for their row in the list. */}
      <PaymentResultBanner
        result={paymentResult}
        verifying={verifyingPayment}
        onDismiss={dismissPaymentResult}
        className="mb-4"
      />

      {/* Summary bar. A parent's list is only their own, so it says so and counts no seats. */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className={`text-sm ${theme.textSecondary}`}>
          {seesEveryone
            ? <><span className="font-semibold">{enrollments.length}</span> enrolled</>
            : <>Your registrations: <span className="font-semibold">{enrollments.length}</span></>}
        </span>
        {seesEveryone && program?.maxParticipants != null && (
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
            enrollments.length >= program.maxParticipants
              ? 'bg-red-100 text-red-700'
              : 'bg-green-100 text-green-700'
          }`}>
            {Math.max(0, program.maxParticipants - enrollments.length)} seats left
          </span>
        )}
        {seesEveryone && program?.maxParticipants == null && (
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
            Unlimited capacity
          </span>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.textMuted}`} />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search students..."
          className={`w-full pl-9 pr-4 py-2 text-sm rounded-lg border ${theme.border} ${theme.input} outline-none focus:ring-2 focus:ring-blue-500`}
        />
      </div>

      {/* Empty state */}
      {filtered.length === 0 ? (
        <div className={`text-center py-12 ${theme.card} rounded-xl border ${theme.border}`}>
          <Users size={36} className={`mx-auto mb-3 ${theme.textMuted}`} />
          <p className={`text-sm font-medium ${theme.text}`}>
            {search
              ? 'No students match your search'
              : (seesEveryone ? 'No students enrolled' : 'You have not registered anybody yet')}
          </p>
          <p className={`text-xs ${theme.textMuted} mt-1`}>
            {search
              ? 'Try a different name.'
              : (seesEveryone ? 'Use the Assign button to enroll students.' : 'Register from the program to see your places here.')}
          </p>
        </div>
      ) : (
        <div className={`${theme.card} rounded-xl border ${theme.border} overflow-hidden`}>
          {/* Table header */}
          {/* bg-gray-50 was hardcoded here, so the header stayed near-white on
              every dark theme while the muted label colour went light with the
              theme — light text on a light strip. */}
          <div className={`hidden md:grid md:grid-cols-4 gap-2 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide ${theme.textMuted} border-b ${theme.border} ${theme.backgroundSecondary}`}>
            <span className="col-span-2">Student</span>
            <span>Enrolled On</span>
            <span>Status</span>
          </div>

          <div className="divide-y divide-gray-100">
            {filtered.map((enr) => {
              const fullName = `${enr.firstName} ${enr.lastName}`;
              return (
                <div key={enr.memberId}>
                  <div className="px-4 py-3 flex flex-col md:grid md:grid-cols-4 gap-1 md:gap-2 md:items-center">
                    {/* Student */}
                    <div className="col-span-2 flex items-center gap-2.5">
                      <Avatar member={enr} />
                      <div className="min-w-0">
                        <p className={`text-sm font-medium ${theme.text} truncate`}>{fullName}</p>
                        {enr.email && (
                          <p className={`text-xs ${theme.textMuted} truncate`}>{enr.email}</p>
                        )}
                      </div>
                    </div>

                    {/* Enrolled on + payment */}
                    <div className="min-w-0">
                      <p className={`text-xs ${theme.textMuted}`}>{formatDate(enr.enrolledAt)}</p>
                      <PaymentStatusBadge method={enr.paymentMethod} status={enr.paymentStatus} amountDue={enr.amountDue}
                        className="mt-0.5"
                      />
                    </div>

                    {/* Status + actions */}
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">
                        Active
                      </span>
                      {/* A card payment that never finished. The row used to
                          say "Unpaid" and stop there — no way to retry, no way
                          to pay another way, and no way to say the money had
                          in fact gone. */}
                      {enr.openDispute ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-pink-300 text-pink-600 text-[11px] font-semibold"
                          title={`Dispute raised ${formatDate(enr.openDispute.raisedAt)} — being looked at`}
                        >
                          <ShieldAlert size={11} />
                          Dispute open
                        </span>
                      ) : needsCardPayment(enr) && (canPayFor(enr) || canManage) && (
                        <button
                          onClick={() => setRecoveringId(
                            recoveringId === enr.enrollmentId ? null : enr.enrollmentId,
                          )}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${theme.border} ${theme.text} ${theme.cardHover} text-[11px] font-semibold transition`}
                          title="This card payment never completed — see what can be done"
                        >
                          <Wrench size={11} />
                          Resolve payment
                        </button>
                      )}
                      {canWithdraw(enr) && (
                        <button
                          onClick={() => setConfirmRemoveId(enr.memberId)}
                          className="ml-auto px-2 py-0.5 rounded border border-pink-200 text-pink-600 dark:text-pink-400 text-xs font-medium hover:bg-pink-50 dark:hover:bg-pink-900/20 transition"
                          title="Withdraw this student and return any fee"
                        >
                          Withdraw
                        </button>
                      )}
                    </div>
                  </div>

                  {recoveringId === enr.enrollmentId && (
                    <UnpaidPaymentActions
                      kind="program"
                      registrationId={enr.enrollmentId}
                      // Stripe collects from whoever is at the keyboard, so a
                      // coach pressing "Pay now" on a parent's row would be
                      // paying out of their own pocket. The server refuses it
                      // too; this is so it is never offered.
                      canPayByCard={canPayFor(enr)}
                      returnPath={payReturnPath}
                      onResolved={reloadEnrollments}
                      onClose={() => setRecoveringId(null)}
                    />
                  )}

                  {/* Inline confirm */}
                  {confirmRemoveId === enr.memberId && (
                    <div className="px-4 pb-3 flex items-center gap-2">
                      {/* Says what will happen to the money, because it is the
                          half a confirm dialog usually leaves out — and the
                          half that cannot be undone. */}
                      <p className={`text-xs ${theme.text} font-medium`}>
                        Withdraw {fullName} from this programme? Their place is given
                        up and any fee already paid is returned.
                      </p>
                      <button
                        onClick={() => handleWithdraw(enr.memberId, fullName)}
                        disabled={removing === enr.memberId}
                        className="px-2 py-0.5 text-xs rounded bg-pink-500 text-white hover:bg-pink-600 disabled:opacity-50 transition flex-shrink-0"
                      >
                        {removing === enr.memberId ? 'Withdrawing…' : 'Confirm'}
                      </button>
                      <button
                        onClick={() => setConfirmRemoveId(null)}
                        className={`px-2 py-0.5 text-xs rounded border ${theme.border} ${theme.text} ${theme.cardHover} transition`}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramRosterPanel;
